import { useEffect, useRef, useState } from 'react';
import styled from "styled-components";

import VBLeaflet from '@vb-js-commons/new/vbLeaflet';
import { swapCoords } from '@vb-js-commons/new/leafletConfig';

import EntryDetailsMap from './components/EntryDetailMap';
import { Icon, TwDropdown, Textarea, Button, Text } from '@ui-component-library';
import { Chip } from './components/Chip';
import { ErrorModal } from '../../../modals';

import { useDFR } from '../context/DFRContext';

import { sendEvent } from '@services/main';
import { useOpenDroneCheckout } from '@components/Checkout/config/DroneCheckout/DroneCheckoutContext';
import { useNavigationStore } from '@store/NavigationStore';
import { usePropsDataStore } from '@store/PropsDataStore';
import { useDemoMinskogStore } from '@store/DemoMinskogStore';
import { DEMO_PROMPTS } from '../constants/demoPrompts';

const RoundedDiv = styled.div`
    padding: 16px;
    border-radius: 16px;
`

const LoggItem = styled.div`
    background: white;
    border: 1px solid #E2E1E0;
    border-radius: 16px;
    padding: 16px;
    margin-bottom: 16px;
`

export default function EntryDetail(props) {
    const DFRContext = useDFR();
    const { openCheckout } = useOpenDroneCheckout();
    const { view, setView } = useNavigationStore()
    const { props: properties } = usePropsDataStore();
    const { DemoBlockFunctionWrapper, isDemo } = useDemoMinskogStore();

    const leaflet = useRef(null);

    const [loading, setLoading] = useState(['editButtons', 'createComment', 'createState',]);
    const [newComment, setNewComment] = useState({});
    const [editComment, setEditComment] = useState([]);
    const [entry, setEntry] = useState(props.entry ?? history.state.entry);
    const [statusSuccess, showStatusSuccess] = useState(false)
    const [rtcSend, setRtcSend] = useState(false)

    const [errorModal, setErrorModal] = useState(false)

    const currentState = entry.states.filter(s => !s.deleted)[0]

    const waitForMapThenSetData = async () => {
        if(!leaflet.current.state.map) {
            setTimeout(() => waitForMapThenSetData(), 100)
        } else {
            setGeom()
        }
    }

    const waitForData = async () => {
        const isReady = properties.some(p => p.ref == entry.ref && p.status === 'data' && p.data && p.ready)

        if(!isReady) {
            setTimeout(() => waitForData(), 100)
        } else {
            if(currentState.state == 'unseen' && !isDemo) {
                await DFRContext.addState(entry.id, {state: 'seen'})
            }
            setLoading([])
        }
    }

    useEffect(() => {
        waitForData()
    }, []);

    useEffect(() => {
        const updatedEntry = DFRContext.entries.find(e => e.id == entry.id)
        if(updatedEntry) {
            setEntry(updatedEntry)
            history.replaceState({...history.state, entry: updatedEntry}, '', '');
        }
    }, [DFRContext.entries])

    useEffect(() => {
        if(entry.geom && !view.showMap) {
            waitForMapThenSetData()
        }
    }, [view.showMap])

    const setGeom = () => {
        let map = leaflet.current.state.map
        let geomFG = L.featureGroup([])
        entry.geom.forEach((geom) => {
            
            if(geom.type === 'Polygon') {
                const color = '#bb0000'

                const baseStyle = {
                    color: color,
                    fillColor: color,
                    opacity: 1,
                    fillOpacity: 0.5,
                    weight: 2,
                    type: 'polygon'
                }


                let layer = L.polygon(swapCoords(geom.coordinates), baseStyle)
                layer.addTo(geomFG)
            }

            if(geom.type === 'LineString') {
                const color = '#bb0000'

                const baseStyle = {
                    color: color,
                    fillColor: color,
                    opacity: 1,
                    fillOpacity: 0.5,
                    weight: 2,
                }

                const lineString = {
                    type: "Feature",
                    geometry: geom
                }

                let layer = L.geoJSON(lineString, baseStyle)
                layer.addTo(geomFG)
            }

            if(geom.type === 'MultiPolygon') {
                const color = '#bb0000'

                const baseStyle = {
                    color: color,
                    fillColor: color,
                    opacity: 1,
                    fillOpacity: 0.5,
                    weight: 2,
                }
                const multiPolygon = {
                    type: "Feature",
                    geometry: geom
                };
                
                let layer = L.geoJSON(multiPolygon, baseStyle)
                layer.addTo(geomFG)
            }

            if(geom.type === 'MultiPoint') {
                const color = '#bb0000'

                const baseStyle = {
                    color: color,
                    fillColor: color,
                    opacity: 1,
                    fillOpacity: 0.5,
                    weight: 2,
                }
                const multiPoint = {
                    type: "Feature",
                    geometry: geom
                };
                
                let layer = L.geoJSON(multiPoint, baseStyle)
                layer.addTo(geomFG)
            }
            
            if(geom.type === 'Point') {
                const icon = L.icon({
                    iconUrl: `${Window.static_path}img/map-markers/marker-location.svg`,
                    iconSize: [40, 57],
                    shadowSize: [40, 57],
                    iconAnchor: [20, 57],
                    shadowAnchor: [40, 57],
                    popupAnchor: [0, -32]
                })

                let marker = L.marker([geom.coordinates[1], geom.coordinates[0]], { icon: icon, type: 'marker' })

                marker.addTo(geomFG)
            }

            
        })
        leaflet.current.setLayers(geomFG)
        geomFG.addTo(map)
        
        map.fitBounds(geomFG.getBounds())
    }

    const fetchCTA = () => {
        const buyDroneList = ['Hög vind i riskområde', 'Aktiv brand - möjlig skogsbrand', 'Möjlig höjdförändring i området', 'Granbarkborre / Döda träd']
        const beContactedList = ['Avverkningsanmälan i närheten']

        if(buyDroneList.includes(entry.title)) {
            return <Button variant="primary" className='my-2' onClick={openCheckout} >
                Köp drönarinventering
            </Button>
        }
        if(beContactedList.includes(entry.title)) {
            return rtcSend == 'send' ? <div className='flex mt-2'>
                    <Icon name='CheckOutlined' className="fill-success-900" size='medium'/>
                    <Text variant='text-xs/leading-normal/medium' className='ml-1 text-success-900'>Vi kommer att kontakta dig.</Text>
                </div> : <div className='flex items-center gap-2 mt-2'>
                    <Button disabled={rtcSend == 'loading'} onClick={async () => {
                        setRtcSend('loading')
                        const resp = await sendEvent('request_to_contact', {source: 'dfr_entry_detail_cta', entry_id: entry.id, entry_title: entry.title})
                        if(resp.ok) {
                            setRtcSend('send')
                        } else {
                            setErrorModal(true)
                            setRtcSend(false)
                        }
                    }}>Bli kontaktad</Button>
                    {rtcSend == 'loading' && <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin align-middle" role="status" />}
                </div>
        }
        return <></>
    }

    const addLoading = string => {
        let loadingSet = new Set(loading)
        loadingSet.add(string)
        setLoading([...loadingSet])
    }

    const updateState = async update => {
        DemoBlockFunctionWrapper(async () => {
            addLoading(`updateState${update.id}`)
            await DFRContext.updateState(entry.id, update)
            setLoading(prevLoading => prevLoading.filter(i => i !== `updateState${update.id}`))
        }, DEMO_PROMPTS.SAVE_CHANGES)
    }

    const addNewState = async state => {
        DemoBlockFunctionWrapper(async () => {
            addLoading('createState')
            const addStateResp = await DFRContext.addState(entry.id, state)
            if(addStateResp) {
                showStatusSuccess(true)
            } else {
                // Do a error handling thing here. (Error modal probably)
            }
            setLoading(prevLoading => prevLoading.filter(i => i !== 'createState'))
        }, DEMO_PROMPTS.SAVE_CHANGES)
    }

    const updateComment = async update => {
        DemoBlockFunctionWrapper(async () => {
            addLoading(`updateComment${update.id}`)
            await DFRContext.updateComment(entry.id, update)
            setLoading(prevLoading => prevLoading.filter(i => i !== `updateComment${update.id}`))
        }, DEMO_PROMPTS.SAVE_CHANGES)
    }

    const addNewComment = async () => {
        DemoBlockFunctionWrapper(async () => {
            addLoading('createComment')
            await DFRContext.addComment(entry.id, newComment)
            setNewComment({})
            setLoading(prevLoading => prevLoading.filter(i => i !== 'createComment'))
        }, DEMO_PROMPTS.SAVE_CHANGES)
    }

    const handleHistory = () => {
        const newView = {...view, showMap: true}
        history.pushState(newView, '', '')
        setView(newView)
    }

    const Spinner = ({ className = '' }) => (
        <span className={`inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent align-middle ${className}`} role="status" />
    )

    const renderLoggItem = item => {
        const name = item.text ? 'Kommentar' : 'Status'
        const title = item.text ? 'Kommentar' : 'Status uppdatering'
        const loadingCheck = item.text ? `updateComment${item.id}` : `updateState${item.id}`
        const action = item.text ? updateComment : updateState

        return <LoggItem>
            <div className='flex flex-col gap-3'>
                {item.deleted ? <div className='flex items-center justify-between gap-3'>
                    <Text variant='text-xs/leading-normal/normal'>Borttagen {name}</Text>

                    {(loading.includes('editButtons') || loading.includes(loadingCheck)) ? <Spinner /> : <Text onClick={() => action({id: item.id, deleted: false})} className='green-pine-500 underline' style={{cursor: 'pointer'}} variant='text-xs/leading-normal/normal'>ångra val</Text>}
                </div> : <>
                    <div className='flex items-center gap-2'>
                        <Icon name={`${item.state ? 'ArrowsUpdate' : 'CommentBoxSimple'}`} size="large" />
                        <Text variant='text-sm/leading-normal/medium'>{title}</Text>
                    </div>

                    {!item.state && editComment.find(e => e.id == item.id) ? <>
                        <Textarea className='bg-white' value={editComment.find(e => e.id == item.id).text} onChange={(value) => setEditComment(editComment.map(c => c.id == item.id ? {...c, text: value} : c))} />

                        <div className='flex items-center gap-2'>
                            <Button variant='secondary' onClick={() => setEditComment(editComment.filter(c => c.id != item.id))} disabled={loading.includes(loadingCheck)}>Avbryt</Button>
                            <Button onClick={async () => {await action(editComment.find(e => e.id == item.id)); setEditComment(editComment.filter(c => c.id != item.id))}} disabled={loading.includes(loadingCheck)}>Uppdatera</Button>
                            {loading.includes(loadingCheck) && <Spinner />}
                        </div>
                    </> : <>
                        <Text className='my-1' variant='text-sm/leading-normal/normal'>{item.state ? `Uppdaterat status till ${item.text_repr}` : item.text}</Text>

                        <div className='flex items-center justify-between gap-3'>
                            <Text className='pebble-500' variant='text-xs/leading-normal/normal'>{item.created_at.slice(0, 10)}</Text>

                            {(loading.includes('editButtons') || loading.includes(loadingCheck)) ? <Spinner /> : <div className='flex items-center gap-2'>
                                {item.text && <div role='button' className='flex items-center' onClick={() => setEditComment([...editComment, {id: item.id, text: item.text}])}>
                                    <Icon name='EditPenSimple' size="medium" />
                                </div>}

                                <RoundedDiv className='hidden'>
                                    <Text variant='text-xs/leading-normal/medium'>Avbryt</Text>
                                    <Text variant='text-xs/leading-normal/medium'>Ta bort</Text>
                                </RoundedDiv>

                                {(item.text || (item.state && entry.states.filter(s => !s.deleted).length > 1)) && <div role='button' className='flex items-center' onClick={() => action({id: item.id, deleted: true})}>
                                    <Icon name='TrashBinSimple' size="medium" />
                                </div>}
                            </div>}
                        </div>
                    </>}
                </>}
            </div>
        </LoggItem>
    }

    const combinedUpdates = () => {
        const combined = [...entry.comments, ...entry.states]

        return combined.sort((a, b) => {
            return new Date(b.created_at) - new Date(a.created_at);
        })
    }

    const renderDescription = desc => {
        const urlRegex = /(https?:\/\/\S+)/g;
        const parts = desc.split(urlRegex);

        return parts.map((part, index) => {
            return /(https?:\/\/\S+)/.test(part) ? <a className='green-spruce-700 font-bold' href={part} key={index} target="_blank">{part}</a> : part;
        });
    }

    return <>
        {view.showMap ? <EntryDetailsMap geom={entry.geom} border={properties.find(p => p.ref == entry.ref)?.border} /> :
        <div className={`relative ${!view.showMap && 'p-3'}`} style={{maxWidth: '960px'}}>
            <div className='flex flex-col'>
                <div role='button' onClick={() => window.history.back()} className='flex w-fit'>
                    <Icon name="ChevronDownSimple" size="large" className="rotate-90" />
                    <Text variant="text-base/leading-normal/normal">Tillbaka</Text>
                </div>
            </div>

            {entry && <div className='flex flex-wrap mt-4'>
                <div className='w-full lg:w-7/12 lg:pr-4'>
                    <div className='flex justify-between items-center gap-2'>
                        <Chip type={currentState.state} />
                        <Text variant='text-xs/leading-normal/medium' className='text-cloud-900'>{entry.designation}</Text>
                    </div>
                    

                    <Text className='mt-4' variant='text-xl/leading-normal/medium'>{entry.title}</Text>

                    <Text className='my-4' variant='text-lg/leading-normal/normal'>{renderDescription(entry.description)}</Text>

                    {entry.geom && <div role='button' onClick={() => handleHistory()} style={{height: '450px'}}>
                        <VBLeaflet ref={leaflet} id='map_karta' baseMap='Mapbox Satelite' mapOptions={{interactive: false, dragging: false, touchZoom: false, scrollWheelZoom: false, doubleClickZoom: false, boxZoom: false, keyboard: false, tap: false, zoomControl: false}} />
                    </div>}

                    {entry.action && <div className='flex rounded bg-cloud-200 mt-4' style={{padding: '12px', border: '1px solid #EBEBEB'}}>
                        <div style={{marginRight: '12px', marginTop: '3px'}}>
                            <Icon name='InfoFilled' className="fill-pebble-500" size='large' />
                        </div>

                        <div>
                            <Text variant='text-sm/leading-normal/medium'>Rekommenderad åtgärd</Text>

                            <Text variant='text-sm/leading-normal/normal' className='text-pebble-500'>{entry.action}</Text>

                            {fetchCTA()}
                        </div>
                    </div>}
                </div>

                <div className='w-full lg:w-5/12 mt-4 lg:mt-0'>
                    <RoundedDiv className='bg-stone-50 mb-4'>
                        <Text variant="text-base/leading-normal/normal" className='mb-1'>Status på ärende</Text>

                        <TwDropdown value={currentState.state} options={DFRContext.states} onChange={(value) => {value != currentState.state && addNewState({state: value})}} disabled={loading.includes('createState')} onFocus={() => showStatusSuccess(false)} />

                        {statusSuccess && <div className='flex pl-2'>
                            <Icon name='CheckOutlined' className="fill-success-900" size='small'/>
                            <Text variant='text-2xs/leading-normal/normal' className='ml-1 text-success-900'>Status har uppdaterats</Text>
                        </div>}
                    </RoundedDiv>

                    <RoundedDiv className='bg-stone-50'>
                        <Text variant='text-base/leading-normal/normal' className='mb-1'>Lämna en kommentar</Text>

                        <Textarea value={newComment?.text} className='bg-white' onChange={(value) => setNewComment({...newComment, text: value})} />

                        {/*<Text variant='text-sm/leading-normal/medium'>Ställ in en påminnelse</Text>
                        <Button className='my-2' variant='secondary' label='Välj datum' prefixIcon='DateCalendarSimple' fullWidth />*/}

                        <Button onClick={addNewComment} fullWidth disabled={loading.includes('createComment')}>Lägg till kommentar</Button>

                        <Text className='mb-1 mt-4' variant='text-base/leading-normal/normal'>Logg</Text>

                        {combinedUpdates().map(update => {
                            return renderLoggItem(update)
                        })}
                    </RoundedDiv>
                </div>
            </div>}
        </div>}

        {/* ERROR Modal */}
        <ErrorModal show={errorModal} onToggle={() => setErrorModal(false)} />
    </>
}